import { i18n } from 'shared/i18n';
import { useState } from 'react';
import { Modal, Spinner } from 'app/components';
import { useApplyTheme, useThemeDiff, themeApplyQuestions } from 'entities/theme';

type ApplyScope = 'full' | 'variables-only';
type ConflictStrategy = 'overwrite' | 'keep';

interface ApplyThemeDialogProps {
  show: boolean;
  onClose: () => void;
  theme: { id: string; name: string } | null;
  documentSuperId: number | string;
}

const classList = (names: string[]) =>
  names
    .slice()
    .sort()
    .map((name) => `.${name}`)
    .join(', ');

export function ApplyThemeDialog({ show, onClose, theme, documentSuperId }: ApplyThemeDialogProps) {
  const [applyScope, setApplyScope] = useState<ApplyScope>('full');
  const [conflictStrategy, setConflictStrategy] = useState<ConflictStrategy>('keep');

  const diff = useThemeDiff({ themeId: theme?.id, documentSuperId, enabled: show });
  const { mutate: applyTheme, isPending: isLoading } = useApplyTheme();

  const handleApply = () => {
    if (!theme) return;
    applyTheme({ themeId: theme.id, documentSuperId, applyScope, conflictStrategy }, { onSuccess: onClose });
  };

  if (!show) {
    return null;
  }

  if (!diff.isSuccess || !diff.data) {
    return (
      <Modal titleLabelKey="theme:apply-title" show={show} onClose={onClose}>
        <Spinner />
      </Modal>
    );
  }

  const { documentVariableCount, sharedVariableCount, changedVariableCount, newClasses, conflicts, missingFonts } = diff.data;
  const questions = themeApplyQuestions(diff.data);

  return (
    <Modal titleLabelKey="theme:apply-title" show={show} onClose={onClose}>
      <div className="d-flex flex-column gap-r-1">
        <div className="d-flex flex-column">
          <span>{i18n('theme:apply.variable-count').replace('{total}', String(documentVariableCount))}</span>
          <span>{i18n('theme:apply.variable-in-theme').replace('{shared}', String(sharedVariableCount))}</span>
          <span>{i18n('theme:apply.variable-changed').replace('{changed}', String(changedVariableCount))}</span>
        </div>

        {questions.askApplyScope && (
          <fieldset className="d-flex flex-column gap-r-0-5">
            <legend className="fs-6 fw-medium">{i18n('theme:apply.scope-question')}</legend>

            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                name="theme-apply-scope"
                id="theme-apply-scope-full"
                checked={applyScope === 'full'}
                onChange={() => setApplyScope('full')}
              />
              <label className="form-check-label" htmlFor="theme-apply-scope-full">
                {i18n('theme:apply.scope-full')}
                <span className="d-block text-muted fs-7">{i18n('theme:apply.scope-full-help')}</span>
              </label>
            </div>

            {applyScope === 'full' && (
              <div className="ms-4 d-flex flex-column gap-r-0-5">
                {newClasses.length > 0 && (
                  <span className="text-muted fs-7">{i18n('theme:apply.classes-appended').replace('{names}', classList(newClasses))}</span>
                )}
                {questions.noteNoConflicts && <span className="text-muted fs-7">{i18n('theme:apply.no-conflicts')}</span>}
                {questions.noteNoThemeClasses && <span className="text-muted fs-7">{i18n('theme:apply.no-theme-classes')}</span>}

                {questions.askConflicts && (
                  <>
                    <span className="fs-7">{i18n('theme:apply.class-conflicts').replace('{names}', classList(conflicts))}</span>
                    {(
                      [
                        { value: 'keep', labelKey: 'theme:apply.conflict-keep' },
                        { value: 'overwrite', labelKey: 'theme:apply.conflict-overwrite' },
                      ] as const
                    ).map((option) => (
                      <div className="form-check" key={option.value}>
                        <input
                          className="form-check-input"
                          type="radio"
                          name="theme-conflict"
                          id={`theme-conflict-${option.value}`}
                          checked={conflictStrategy === option.value}
                          onChange={() => setConflictStrategy(option.value)}
                        />
                        <label className="form-check-label" htmlFor={`theme-conflict-${option.value}`}>
                          {i18n(option.labelKey)}
                        </label>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}

            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                name="theme-apply-scope"
                id="theme-apply-scope-variables"
                checked={applyScope === 'variables-only'}
                onChange={() => setApplyScope('variables-only')}
              />
              <label className="form-check-label" htmlFor="theme-apply-scope-variables">
                {i18n('theme:apply.scope-variables')}
                <span className="d-block text-muted fs-7">{i18n('theme:apply.scope-variables-help')}</span>
              </label>
            </div>
          </fieldset>
        )}

        {questions.noteFonts && (
          <div className="alert alert-info d-flex flex-row gap-r-0-5 align-items-start py-2 px-3 mb-0">
            <i className="fal fa-circle-info mt-1"></i>
            <span>{i18n('theme:apply.missing-fonts').replace('{fonts}', missingFonts.join(', '))}</span>
          </div>
        )}

        {questions.noteDraft && (
          <div className="d-flex flex-row gap-r-0-5 align-items-start text-muted">
            <i className="fal fa-circle-info mt-1"></i>
            <span>{i18n('theme:apply.pending-draft')}</span>
          </div>
        )}
      </div>

      <div className="d-flex justify-content-end mt-5">
        <button type="button" className="btn" onClick={onClose}>
          {i18n('common:button.cancel')}
        </button>
        <button type="button" className="btn btn-primary" onClick={handleApply} disabled={isLoading}>
          {i18n('theme:apply.confirm')}
        </button>
      </div>
    </Modal>
  );
}
